/**
 * Sign-in requests through the iframe (tss-api #6, D7/D13).
 *
 * The sign-in UI stays in the parent; the parent passes only what the user typed or the WebAuthn result.
 * The iframe makes every sign-in request to TSS with a per-flow secret, PKCE-style:
 * - start: `flow_challenge = base64url(sha256(flow_secret))` (the hash of the base64url string, as in PKCE S256);
 * - finish: `flow_secret`.
 * TSS issues an iframe session only when the secret matches, so nothing the parent page sees during the flow
 * (the OTP code, a passkey assertion, a wallet signature, the Telegram payload, the X code) can be redeemed
 * without it. The secret never leaves the iframe.
 *
 * Wallet: the iframe gets the message to sign from TSS (its nonce is bound to the challenge), the parent asks the
 * wallet to sign it. Telegram and X: the parent opens the popup as before; the iframe starts the flow first (X:
 * the authorization URL the parent opens) and redeems the result. Their starts carry the parent origin
 * (`parent_origin`, the postMessage `event.origin`): TSS accepts only an allowed domain of the project and binds
 * it to the flow, and the popup posts the result to that origin only.
 *
 * The finish keeps the iframe session in the iframe and answers the parent with a dApp token pair.
 */

import { AccountAbstractionError } from '../../lib/errors'
import { iframeSession, type DappTokenPair, type IframeSession, type SessionTokens } from './iframe-session'
import { sdkVersionHeaders } from './sdk-version'

declare const __EMBARK_TSS_URL__: string

/** A flow not finished within this time is forgotten (TSS codes and challenges expire sooner) */
const FLOW_TTL_MS = 15 * 60 * 1000

export type SignInProvider = 'email' | 'passkey' | 'wallet' | 'telegram' | 'x'
export type PasskeyMode = 'login' | 'register'

export type SignInStartRequest =
  | { provider: 'email'; email: string }
  | { provider: 'passkey'; mode?: PasskeyMode; username?: string }
  | { provider: 'wallet'; address: string }
  | { provider: 'telegram' }
  | { provider: 'x' }

/** Telegram Login Widget payload, as the popup gets it */
export interface TelegramPayload {
  id: number
  first_name?: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

export type SignInFinishRequest =
  | { provider: 'email'; code: string }
  | { provider: 'passkey'; credential: unknown }
  | { provider: 'wallet'; signature: string; chainId: number; walletName?: string; walletType?: string }
  | { provider: 'telegram'; payload: TelegramPayload }
  | { provider: 'x'; code: string }

/** Answer to SIGN_IN_START. Email mirrors the TSS response so the parent keeps its error handling. */
export type SignInStartResult =
  | { provider: 'email'; ok: boolean; status: number; data: Record<string, unknown> }
  | { provider: 'passkey'; mode: PasskeyMode; options: Record<string, unknown> }
  | { provider: 'wallet'; message: string }
  | { provider: 'telegram'; parentOrigin: string }
  | { provider: 'x'; url: string }

/** Answer to SIGN_IN_FINISH: the login response with the dApp pair in place of the session tokens */
export interface SignInFinishResult {
  accessToken: string
  refreshToken: string
  userId: string
  expiresIn: number
  hasKeyshare: boolean
  isNewUser?: boolean
  displayName?: string | null
  avatar?: string | null
  providers?: string[]
}

interface PendingFlow {
  provider: SignInProvider
  mode?: PasskeyMode
  flowSecret: string
  projectId: string
  email?: string
  challengeId?: string
  /** wallet: the address and the message TSS prepared */
  address?: string
  message?: string
  /** telegram: the TSS flow id */
  flowId?: string
  startedAt: number
}

/** X authorization endpoints the parent may open: anything else in a TSS answer is refused */
const X_AUTHORIZE_ORIGINS = ['https://x.com', 'https://twitter.com']

export function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** 32 random bytes, base64url without padding (43 characters) */
export function generateFlowSecret(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)))
}

/** base64url(SHA-256(UTF-8 bytes of the flow secret string)), as TSS checks it */
export async function flowChallengeOf(flowSecret: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(flowSecret))
  return base64url(new Uint8Array(digest))
}

export function isXAuthorizeUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return X_AUTHORIZE_ORIGINS.includes(url.origin) && url.pathname === '/i/oauth2/authorize'
  } catch {
    return false
  }
}

/** The Login Widget fields TSS checks the hash over, exactly as received (absent optional fields stay absent) */
function telegramFields(payload: TelegramPayload | undefined): Record<string, string | number> | null {
  if (!payload || typeof payload.id !== 'number' || typeof payload.auth_date !== 'number' || typeof payload.hash !== 'string') return null
  const fields: Record<string, string | number> = {
    id: payload.id,
    first_name: typeof payload.first_name === 'string' ? payload.first_name : '',
    auth_date: payload.auth_date,
    hash: payload.hash
  }
  for (const key of ['last_name', 'username', 'photo_url'] as const) {
    if (typeof payload[key] === 'string' && payload[key]) fields[key] = payload[key] as string
  }
  return fields
}

function defaultTssUrl(): string {
  return (typeof __EMBARK_TSS_URL__ !== 'undefined' && __EMBARK_TSS_URL__) || 'https://api.embarkai.io/tss'
}

async function readBody(response: Response): Promise<{ text: string; json: Record<string, any> | null }> {
  const text = await response.text().catch(() => '')
  try {
    const parsed = JSON.parse(text)
    return { text, json: parsed && typeof parsed === 'object' ? parsed : null }
  } catch {
    return { text, json: null }
  }
}

function signInError(message: string, status: number, body: Record<string, any> | null): AccountAbstractionError {
  return new AccountAbstractionError(message, (typeof body?.error === 'string' && body.error) || 'SIGN_IN_FAILED', {
    status,
    ...(body?.retryAfter !== undefined ? { retryAfter: body.retryAfter } : {}),
    ...(body?.attemptsRemaining !== undefined ? { attemptsRemaining: body.attemptsRemaining } : {})
  })
}

export class IframeSignIn {
  /** one pending flow per parent origin: the latest start wins, as at TSS */
  private flows = new Map<string, PendingFlow>()

  constructor(
    private readonly session: IframeSession = iframeSession,
    private readonly tssUrl: () => string = defaultTssUrl
  ) {}

  /** Whether a flow is pending for this origin (tests, diagnostics); never exposes the secret */
  hasPendingFlow(origin: string): boolean {
    return !!this.activeFlow(origin)
  }

  async start(request: SignInStartRequest, origin: string, projectId: string): Promise<SignInStartResult> {
    const flowSecret = generateFlowSecret()
    const flowChallenge = await flowChallengeOf(flowSecret)

    if (request?.provider === 'email') {
      const email = typeof request.email === 'string' ? request.email.trim() : ''
      if (!email) throw new AccountAbstractionError('email is required', 'INVALID_REQUEST')

      const response = await this.post('/api/auth/email/send-code', projectId, { email, flow_challenge: flowChallenge })
      const { json } = await readBody(response)
      if (response.ok) {
        this.flows.set(origin, { provider: 'email', flowSecret, projectId, email, startedAt: Date.now() })
      }
      return { provider: 'email', ok: response.ok, status: response.status, data: json ?? {} }
    }

    if (request?.provider === 'passkey') {
      const mode: PasskeyMode = request.mode === 'register' ? 'register' : 'login'
      const path = mode === 'register' ? '/api/auth/passkey/register/begin' : '/api/auth/passkey/login/begin'
      const body: Record<string, unknown> = { flow_challenge: flowChallenge, parent_origin: origin }
      if (typeof request.username === 'string' && request.username) body.username = request.username

      const response = await this.post(path, projectId, body)
      const { text, json } = await readBody(response)
      if (!response.ok || !json) {
        const prefix = mode === 'register' ? 'Begin registration failed' : 'Begin auth failed'
        throw signInError(`${prefix}: ${response.status} ${text}`, response.status, json)
      }
      this.flows.set(origin, {
        provider: 'passkey',
        mode,
        flowSecret,
        projectId,
        challengeId: typeof json.challengeId === 'string' ? json.challengeId : undefined,
        startedAt: Date.now()
      })
      return { provider: 'passkey', mode, options: json }
    }

    if (request?.provider === 'wallet') {
      const address = typeof request.address === 'string' ? request.address.trim() : ''
      if (!/^0x[0-9a-fA-F]{40}$/.test(address)) throw new AccountAbstractionError('address is required', 'INVALID_REQUEST')

      const response = await this.post('/api/auth/wallet/nonce', projectId, { address, flow_challenge: flowChallenge })
      const { text, json } = await readBody(response)
      if (!response.ok || typeof json?.message !== 'string') {
        throw signInError(`Wallet sign-in start failed: ${response.status} ${json?.message || text}`, response.status, json)
      }
      this.flows.set(origin, { provider: 'wallet', flowSecret, projectId, address, message: json.message, startedAt: Date.now() })
      return { provider: 'wallet', message: json.message }
    }

    if (request?.provider === 'telegram') {
      const response = await this.post('/api/auth/telegram/start', projectId, { flow_challenge: flowChallenge, parent_origin: origin })
      const { text, json } = await readBody(response)
      if (!response.ok || typeof json?.flow_id !== 'string') {
        throw signInError(`Telegram sign-in start failed: ${response.status} ${json?.message || text}`, response.status, json)
      }
      this.flows.set(origin, { provider: 'telegram', flowSecret, projectId, flowId: json.flow_id, startedAt: Date.now() })
      return { provider: 'telegram', parentOrigin: typeof json.parent_origin === 'string' ? json.parent_origin : origin }
    }

    if (request?.provider === 'x') {
      const response = await this.post('/api/auth/x/start', projectId, { flow_challenge: flowChallenge, parent_origin: origin })
      const { text, json } = await readBody(response)
      const url = typeof json?.url === 'string' ? json.url : ''
      if (!response.ok || !url) {
        throw signInError(`X sign-in start failed: ${response.status} ${json?.message || text}`, response.status, json)
      }
      if (!isXAuthorizeUrl(url)) throw new AccountAbstractionError('Unexpected X authorization URL', 'SIGN_IN_FAILED')
      this.flows.set(origin, { provider: 'x', flowSecret, projectId, startedAt: Date.now() })
      return { provider: 'x', url }
    }

    throw new AccountAbstractionError('Unsupported sign-in provider', 'UNSUPPORTED_PROVIDER')
  }

  async finish(request: SignInFinishRequest, origin: string, projectId: string): Promise<SignInFinishResult> {
    const flow = this.activeFlow(origin)
    if (!flow || flow.provider !== request?.provider || flow.projectId !== projectId) {
      throw new AccountAbstractionError('No sign-in in progress, please start again', 'FLOW_NOT_STARTED')
    }

    let response: Response
    let failurePrefix: string
    if (request.provider === 'email') {
      const code = typeof request.code === 'string' ? request.code.trim() : ''
      if (!code) throw new AccountAbstractionError('code is required', 'INVALID_REQUEST')
      response = await this.post('/api/auth/email/verify-code', projectId, { email: flow.email, code, flow_secret: flow.flowSecret })
      failurePrefix = 'Email verification failed'
    } else if (request.provider === 'wallet') {
      const signature = typeof request.signature === 'string' ? request.signature : ''
      if (!signature || typeof request.chainId !== 'number') {
        throw new AccountAbstractionError('signature and chainId are required', 'INVALID_REQUEST')
      }
      response = await this.post('/api/auth/wallet/login', projectId, {
        address: flow.address,
        message: flow.message,
        signature,
        chainId: request.chainId,
        ...(typeof request.walletName === 'string' ? { walletName: request.walletName } : {}),
        ...(typeof request.walletType === 'string' ? { walletType: request.walletType } : {}),
        flow_secret: flow.flowSecret
      })
      failurePrefix = 'Wallet login failed'
    } else if (request.provider === 'telegram') {
      const payload = telegramFields(request.payload)
      if (!payload) throw new AccountAbstractionError('Telegram payload is required', 'INVALID_REQUEST')
      response = await this.post('/api/auth/telegram/login', projectId, { ...payload, flow_id: flow.flowId, flow_secret: flow.flowSecret })
      failurePrefix = 'Telegram login failed'
    } else if (request.provider === 'x') {
      const code = typeof request.code === 'string' ? request.code.trim() : ''
      if (!code) throw new AccountAbstractionError('code is required', 'INVALID_REQUEST')
      response = await this.post('/api/auth/exchange-code', projectId, { code, flow_secret: flow.flowSecret })
      failurePrefix = 'X login failed'
    } else {
      if (!flow.challengeId || !request.credential) {
        throw new AccountAbstractionError('credential is required', 'INVALID_REQUEST')
      }
      const path = flow.mode === 'register' ? '/api/auth/passkey/register/complete' : '/api/auth/passkey/login/complete'
      response = await this.post(path, projectId, {
        challengeId: flow.challengeId,
        credential: request.credential,
        flow_secret: flow.flowSecret
      })
      failurePrefix = flow.mode === 'register' ? 'Registration failed' : 'Complete authentication failed'
    }

    const { text, json } = await readBody(response)
    if (!response.ok || !json) {
      // the flow stays: a wrong code can be retried with the same secret
      const detail =
        (request.provider === 'passkey' && flow.mode === 'login') || request.provider === 'wallet' ? json?.message || text : text
      // exchange-code reports its error code as `code`
      const body = request.provider === 'x' && json && typeof json.code === 'string' ? { ...json, error: json.code } : json
      throw signInError(`${failurePrefix}: ${response.status} ${detail}`, response.status, body)
    }
    this.flows.delete(origin)

    const login = json as SessionTokens & { isNewUser?: boolean; hasKeyshare?: boolean }
    if (!login.accessToken || !login.refreshToken || !login.userId) {
      throw new AccountAbstractionError('Sign-in response is missing the session', 'SIGN_IN_FAILED')
    }

    // a previous session in this iframe (another user, an older sign-in) is replaced and revoked
    const previous = this.session.getIncludingUnconfirmed()
    if (previous && previous.refreshToken !== login.refreshToken) void this.revoke(previous.accessToken, previous.projectId)

    // unconfirmed until the user passes the consent step (AUTHENTICATE), see IframeSession
    this.session.save(login, projectId, { confirmed: false })
    const pair: DappTokenPair = await this.session.mintDappTokens(projectId)

    return {
      accessToken: pair.accessToken,
      refreshToken: pair.refreshToken,
      userId: login.userId,
      expiresIn: pair.expiresIn,
      hasKeyshare: !!login.hasKeyshare,
      ...(login.isNewUser !== undefined ? { isNewUser: login.isNewUser } : {}),
      displayName: login.displayName ?? null,
      avatar: login.avatar ?? null,
      providers: login.providers ?? []
    }
  }

  /** Forget the pending flow of this origin (sign-out, cancel) */
  cancel(origin: string): void {
    this.flows.delete(origin)
  }

  private activeFlow(origin: string): PendingFlow | null {
    const flow = this.flows.get(origin)
    if (!flow) return null
    if (Date.now() - flow.startedAt > FLOW_TTL_MS) {
      this.flows.delete(origin)
      return null
    }
    return flow
  }

  private post(path: string, projectId: string, body: Record<string, unknown>): Promise<Response> {
    return fetch(`${this.tssUrl()}${path}?projectId=${encodeURIComponent(projectId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...sdkVersionHeaders() },
      body: JSON.stringify(body)
    })
  }

  private async revoke(accessToken: string, projectId?: string): Promise<void> {
    try {
      const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''
      await fetch(`${this.tssUrl()}/api/auth/logout${query}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`, ...sdkVersionHeaders() }
      })
    } catch {}
  }
}
